import { ref } from 'vue';
import { defineStore } from 'pinia';
import type {
  DiscoverQuery,
  Friend,
  FriendRequest,
  UserWithRelation,
} from '@talkvibe/shared';

import { userService } from '../services/user.ts';

/**
 * People: discovery results, the friend list, and pending requests.
 *
 * Discovery is paginated by cursor, so `loadMore` appends rather than
 * replacing - the view keeps what the user has already scrolled past.
 */
export const useUserStore = defineStore('user', () => {
  const discovered = ref<UserWithRelation[]>([]);
  const discoverCursor = ref<string | null>(null);
  const discoverLoading = ref(false);
  const discoverError = ref<string | null>(null);

  const friends = ref<Friend[]>([]);
  const friendsCursor = ref<string | null>(null);
  const friendsLoading = ref(false);

  const incomingRequests = ref<FriendRequest[]>([]);
  const outgoingRequests = ref<FriendRequest[]>([]);
  const requestsLoading = ref(false);

  /** A token so a slow response from an abandoned filter is ignored. */
  let discoverRun = 0;

  async function search(query: DiscoverQuery): Promise<void> {
    const run = ++discoverRun;
    discoverLoading.value = true;
    discoverError.value = null;
    try {
      const page = await userService.discover(query);
      if (run !== discoverRun) return;
      discovered.value = page.items;
      discoverCursor.value = page.nextCursor;
    } catch (error) {
      if (run !== discoverRun) return;
      discovered.value = [];
      discoverCursor.value = null;
      discoverError.value = error instanceof Error ? error.message : 'Search failed.';
    } finally {
      if (run === discoverRun) discoverLoading.value = false;
    }
  }

  async function loadMore(query: DiscoverQuery): Promise<void> {
    if (!discoverCursor.value || discoverLoading.value) return;
    const run = discoverRun;
    discoverLoading.value = true;
    try {
      const page = await userService.discover({ ...query, cursor: discoverCursor.value });
      if (run !== discoverRun) return;
      discovered.value = [...discovered.value, ...page.items];
      discoverCursor.value = page.nextCursor;
    } finally {
      if (run === discoverRun) discoverLoading.value = false;
    }
  }

  async function loadFriends(): Promise<void> {
    friendsLoading.value = true;
    try {
      const page = await userService.friends();
      friends.value = page.items;
      friendsCursor.value = page.nextCursor;
    } finally {
      friendsLoading.value = false;
    }
  }

  async function loadMoreFriends(): Promise<void> {
    if (!friendsCursor.value || friendsLoading.value) return;
    friendsLoading.value = true;
    try {
      const page = await userService.friends(friendsCursor.value);
      friends.value = [...friends.value, ...page.items];
      friendsCursor.value = page.nextCursor;
    } finally {
      friendsLoading.value = false;
    }
  }

  async function loadRequests(): Promise<void> {
    requestsLoading.value = true;
    try {
      const [incoming, outgoing] = await Promise.all([
        userService.friendRequests('incoming'),
        userService.friendRequests('outgoing'),
      ]);
      incomingRequests.value = incoming.items;
      outgoingRequests.value = outgoing.items;
    } finally {
      requestsLoading.value = false;
    }
  }

  /**
   * Sends a connection request and reflects the new state in any list the
   * person appears in, so the button updates without a refetch.
   */
  async function connect(userId: string): Promise<'request_sent' | 'friends'> {
    const { result } = await userService.connect(userId);
    patchConnection(userId, result === 'friends' ? 'friends' : 'request_sent');
    if (result === 'friends') await loadFriends();
    return result;
  }

  async function acceptRequest(requestId: string): Promise<void> {
    await userService.acceptRequest(requestId);
    const request = incomingRequests.value.find((entry) => entry.id === requestId);
    incomingRequests.value = incomingRequests.value.filter((entry) => entry.id !== requestId);
    if (request) patchConnection(request.user.id, 'friends');
    await loadFriends();
  }

  async function rejectRequest(requestId: string): Promise<void> {
    await userService.rejectRequest(requestId);
    incomingRequests.value = incomingRequests.value.filter((entry) => entry.id !== requestId);
  }

  async function cancelRequest(requestId: string): Promise<void> {
    await userService.cancelRequest(requestId);
    outgoingRequests.value = outgoingRequests.value.filter((entry) => entry.id !== requestId);
  }

  function patchConnection(userId: string, connection: UserWithRelation['connection']): void {
    discovered.value = discovered.value.map((entry) =>
      entry.id === userId ? { ...entry, connection } : entry,
    );
  }

  /** Keeps a card's follow state in step without refetching the page. */
  function patchFollow(userId: string, following: boolean): void {
    discovered.value = discovered.value.map((entry) =>
      entry.id === userId
        ? {
            ...entry,
            following,
            followerCount: Math.max(0, entry.followerCount + (following ? 1 : -1)),
          }
        : entry,
    );
  }

  return {
    discovered,
    discoverCursor,
    discoverLoading,
    discoverError,
    friends,
    friendsCursor,
    friendsLoading,
    incomingRequests,
    outgoingRequests,
    requestsLoading,
    search,
    loadMore,
    loadFriends,
    loadMoreFriends,
    loadRequests,
    connect,
    patchFollow,
    acceptRequest,
    rejectRequest,
    cancelRequest,
  };
});
